import { useRef, useState } from 'react';
import { ArrowUpRight, Plane } from 'lucide-react';
import type { Collection, FeaturedLink } from '@/lib/taxonomy';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';

/**
 * A collection's title. Bitcoin's starts with the plain ₿ character (U+20BF) in
 * place of the letter B: ordinary text, set in the system UI font because the
 * display typeface has no ₿ in it. The real word stays in the accessible name.
 */
export function CollectionTitle({ collection: c }: { collection: Collection }) {
  if (c.id === 'bitcoin') {
    return (
      <>
        <span className="sr-only">{c.title}</span>
        <span aria-hidden="true">
          <span className="font-sans">&#x20BF;</span>
          {c.title.slice(1)}
        </span>
      </>
    );
  }
  return <>{c.title}</>;
}

/** Shared visual landmarks for the two event-focused directory entries. */
export function CollectionIcon({ collection, className }: { collection: Collection; className?: string }) {
  if (collection.id === 'wtc-collapse') {
    return (
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        className={className}
      >
        {/* Simplified elevation: approximately 6.5:1 height-to-width, nearly
            equal roof heights, and a broadcast mast on the North Tower only. */}
        <path d="M8.5 30V6h3.75v24M18 30V6.25h3.75V30" />
        <path d="M10.375 1v5" strokeLinecap="round" />
        {/* Fine vertical facade ribs, interrupted by the mechanical-floor bands
            near the thirds of each tower and just beneath the rooflines. */}
        <path
          d="M9.75 8v5.5m0 1.5v6.5m0 1.5v7M11 8v5.5m0 1.5v6.5m0 1.5v7M19.25 8.25v5.25m0 1.5v6.5m0 1.5v7M20.5 8.25v5.25m0 1.5v6.5m0 1.5v7"
          strokeWidth={0.5}
        />
        <path
          d="M8.5 7.75h3.75M18 8h3.75M8.5 14.25h3.75m5.75 0h3.75M8.5 22.25h3.75m5.75 0h3.75"
          strokeWidth={1.25}
        />
        <path d="M6 30h18" strokeLinecap="round" />
      </svg>
    );
  }
  return collection.id === 'planes'
    ? <Plane aria-hidden="true" className={className} strokeWidth={1.75} />
    : null;
}

interface CollectionInfoProps {
  /** The collection to explain, or null when the popup is closed. */
  collection: Collection | null;
  /** Records in that collection. */
  count?: number;
  onClose: () => void;
  /** Show that collection's records. */
  onSelect: (id: string) => void;
}

const primaryButton =
  'flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-foreground px-5 font-engraved text-[11px] font-bold uppercase tracking-[0.2em] text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * The "i" popup: what a collection is and what you will find in it. A bottom
 * sheet on phones (in the thumb's reach, pull down or tap outside to dismiss)
 * and a centred dialog on larger screens.
 */
export function CollectionInfo({ collection: c, count, onClose, onSelect }: CollectionInfoProps) {
  const isMobile = useIsMobile();
  const open = c !== null;
  const [expandedInscription, setExpandedInscription] = useState<string | null>(null);

  // Keep the last collection on screen while the popup animates out.
  const last = useRef<Collection | null>(null);
  if (c) last.current = c;
  const shown = c ?? last.current;
  const external = Boolean(shown?.href);

  const body = shown && (
    <>
      <div className="border-b border-foreground/25 px-5 pb-4 pt-2">
        {/* Radix wants a real Title and Description for screen readers. */}
        {isMobile ? (
          <SheetTitle className="title-inked font-display text-3xl font-normal leading-[1.05] text-foreground">
            <CollectionTitle collection={shown} />
          </SheetTitle>
        ) : (
          <DialogTitle className="title-inked font-display text-3xl font-normal leading-[1.05] text-foreground">
            <CollectionTitle collection={shown} />
          </DialogTitle>
        )}
        <p className="mt-2 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70">
          {shown.caption}
        </p>
        {isMobile ? (
          <SheetDescription className="sr-only">{shown.about}</SheetDescription>
        ) : (
          <DialogDescription className="sr-only">{shown.about}</DialogDescription>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
         <p className="font-display text-lg leading-snug text-foreground">{shown.about}</p>

         {shown.featuredLinks && shown.featuredLinks.length > 0 && (
           <>
             <p className="mt-5 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70">
               Featured sources
             </p>
             <div className="mt-2 space-y-2">
               {shown.featuredLinks.map((link) => (
                 <div key={link.href}>
                   <a
                     href={link.href}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center justify-between gap-3 rounded-lg border border-foreground/25 bg-foreground/5 p-3 text-[14px] leading-snug text-foreground transition-colors hover:bg-foreground/10 hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                   >
                     <span className="flex-1">{link.label}</span>
                     <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground/60" />
                   </a>

                   {/* Show all inscriptions if this is the manifest */}
                   {link.isManifest && link.inscriptions && (
                     <div className="mt-2 rounded-lg border border-foreground/15 bg-background p-4 max-h-[400px] overflow-y-auto">
                       <div className="space-y-2">
                         {link.inscriptions.map((inscription, idx) => (
                           <a
                             key={inscription.id}
                             href={`https://ordiscan.com/inscription/${inscription.id}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="flex items-start justify-between gap-2 p-2 rounded hover:bg-foreground/5 transition-colors group"
                           >
                             <span className="text-[13px] text-foreground/80 group-hover:text-foreground transition-colors flex-1">
                               {idx + 1}. {inscription.title}
                             </span>
                             <span className="text-[12px] font-mono text-foreground/50 group-hover:text-foreground/70 transition-colors whitespace-nowrap">
                               #{inscription.id}
                             </span>
                           </a>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>
               ))}
             </div>
           </>
         )}

         {shown.includes && shown.includes.length > 0 && (
           <>
             <p className="mt-5 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70">
               {external ? 'Good to know' : 'What you will find'}
             </p>
             <ul className="mt-2 divide-y divide-foreground/15 border-y border-foreground/25">
               {shown.includes.map((line) => (
                 <li key={line} className="flex gap-3 py-2 text-[15px] leading-snug text-foreground">
                   <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-foreground" />
                   {line}
                 </li>
               ))}
             </ul>
           </>
         )}
       </div>

      <div className="flex items-center gap-3 border-t border-foreground/25 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {external ? (
          <a href={shown.href} target="_blank" rel="noopener noreferrer" onClick={onClose} className={primaryButton}>
            Open on Ordiscan
            <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
          </a>
        ) : (
          <button type="button" onClick={() => onSelect(shown.id)} className={primaryButton}>
            {typeof count === 'number'
              ? `Show ${count.toLocaleString()} ${count === 1 ? 'record' : 'records'}`
              : 'Show records'}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="h-12 shrink-0 rounded-full border border-foreground/50 px-5 font-engraved text-[11px] font-bold uppercase tracking-[0.2em] text-foreground transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Close
        </button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="max-h-[85svh] gap-0 rounded-t-3xl border-x-0 border-b-0 border-t border-foreground/40 bg-background p-0"
        >
          {/* The grabber, so it reads as a sheet you can pull down. */}
          <span aria-hidden="true" className="mx-auto mb-3 mt-2 h-1 w-10 rounded-full bg-foreground/30" />
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[85svh] w-full max-w-lg flex-col gap-0 overflow-hidden rounded-3xl border border-foreground/40 bg-background p-0 shadow-[0_24px_48px_-16px_rgba(40,25,10,0.55)]"
      >
        <div aria-hidden="true" className="h-5" />
        {body}
      </DialogContent>
    </Dialog>
  );
}
